import {
  Box,
  Button,
  Container,
  Divider,
  Grid,
  TextField,
  Typography,
} from "@mui/material";
import styles from "./AuditLogs.module.scss";
import { AuditLogFilterType } from "../../interface";
import {
  formatRowsForAuditLogPDF,
  formatValueForCSVAuditLog,
  generateExportFileName,
  getRangeDate,
  searchAuditLogsData,
} from "../../utils/helper";
import {
  Suspense,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { DateRange } from "../../type/enums";
import AuditLogFilter from "./AuditLogFilter/AuditLogFilter";
import Footer from "../../components/Footer/Footer";
import useAuditFilterLogDetails from "./service/useAuditFilterLogDetailsHook/useAuditFilterLogDetails.hook";
import SearchIcon from "@mui/icons-material/Search";
import CustomTabs from "../controls/CustomTabs";
import { TabsAuditConfig } from "./TabsAuditConfig/TabsAuditConfig";
import { ToastHelper } from "../../utils/toastHelper";
import ExportRecord from "../controls/Modals/exportRecords";
import jsPDF from "jspdf";
import {
  defaultAutoTableOptions,
  defaultJSPDFOptions,
  requiredAuditLogColumns,
} from "../../utils/constants";
import ExcelJS from "exceljs";
import { saveAs } from "file-saver";
import { useExportAuditLogsData } from "./service/useExportAuditLogsDataHook/useExportAuditLogsData.hook";

const AuditLogs = () => {
  const [activeTab, setActiveTab] = useState<number>(0);
  const [searchData, setSearchData] = useState<string>("");
  const [startDate, endDate] = getRangeDate("thisMonth" as DateRange);
  const toastHelper = useMemo(() => new ToastHelper(), []);
  const [exportOpen, setExportOpen] = useState(false);
  const [nextPageKey, setNextPageKey] = useState<string | undefined>("");
  const prevRowsPageKey = useRef<string | undefined>("");
  const [limits, setLimits] = useState({
    all: [0, 50],
  });

  const [tempFilter, setTempFilter] = useState<AuditLogFilterType>({
    dateRange: "thisMonth",
    startDate,
    endDate,
    nextPageKey: "",
    prevPageKey: "",
  });

  const [filter, setFilter] = useState<AuditLogFilterType>(tempFilter);
  const { exportAuditLogsData } = useExportAuditLogsData(tempFilter);
  const {
    auditLogData: allRecord,
    totalCount: allTotal,
    isError: allError,
    isLoading: allLoading,
    nextRowsPageKey: nextKey,
    prevPageKey: prevKey,
  } = useAuditFilterLogDetails(filter, limits.all, nextPageKey);

  //Handle error and  zero records for all tab
  const handleRecordErrors = useCallback(
    (isLoading: boolean, isError: boolean) => {
      if (!isLoading) {
        if (isError) {
          toastHelper.showToast({
            severity: "warn",
            title: "Warning",
            content: "No Records Found",
          });
        }
      }
    },
    [toastHelper],
  );

  useEffect(() => {
    return handleRecordErrors(allLoading, allError);
  }, [allLoading, allError, handleRecordErrors]);

  const allRecordWithSearch = useMemo(
    () => searchAuditLogsData(allRecord, searchData),
    [allRecord, searchData],
  );

  const tabConfig = useMemo(
    () =>
      TabsAuditConfig(
        limits,
        nextKey,
        prevKey,
        allLoading,
        allTotal,
        allRecordWithSearch,
        setLimits,
        setNextPageKey,
      ),
    [limits, allLoading, allTotal, nextKey, prevKey, allRecordWithSearch],
  );

  const applyHandler = () => {
    setLimits({ all: [0, 50] });
    console.log("auditLogs", filter);
    console.log("tempFilter", tempFilter);
    setFilter(tempFilter);
    setNextPageKey("");
    prevRowsPageKey.current = "";
  };

  const handleDataChange = (value: any) => {
    setSearchData(value);
  };

  const handleExportClick = () => {
    setExportOpen(true);
  };

  const convertDataGridToCSV = async (fileName: string) => {
    const data = await exportAuditLogsData();

    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet("AuditLogs");

    const headerRow = requiredAuditLogColumns.map((col) => col.headerName);
    worksheet.addRow(headerRow).font = { bold: true, size: 11 };
    data.forEach((row: any) => {
      const rowData = requiredAuditLogColumns.map((col) =>
        formatValueForCSVAuditLog(row[col.field]),
      );
      worksheet.addRow(rowData);
    });

    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });
    saveAs(blob, fileName);
  };

  const downloadCSV = async () => {
    const dynamicToast = new ToastHelper(true, "Please wait...");
    try {
      dynamicToast.updateToast({
        severity: "info",
        title: "Processing",
        content: "Export in progress...",
        showClose: false,
        options: {
          isLoading: true,
          position: "top-center",
          autoClose: 3000,
        },
      });
      const fileName = generateExportFileName("xlsx");
      await convertDataGridToCSV(fileName);

      dynamicToast.updateToast({
        severity: "success",
        content: `Export successful: ${fileName}`,
      });
    } catch (error) {
      console.error(error);
      dynamicToast.updateToast({
        severity: "error",
        content: "Export failed due to server error. Please try again.",
      });
    }
  };

  const downloadPDF = async (selectedHeaders: string[]) => {
    const dynamicToast = new ToastHelper(true, "Please wait...");
    try {
      dynamicToast.updateToast({
        severity: "info",
        content: "Export in progress...",
        title: "Processing",
        showClose: false,
        options: {
          isLoading: true,
          position: "top-center",
          autoClose: 3000,
        },
      });
      const doc = new jsPDF({ ...defaultJSPDFOptions });
      const data = await exportAuditLogsData();

      let tableRows = formatRowsForAuditLogPDF(data, selectedHeaders);
      const maxWidth = 400;

      tableRows = tableRows.map((row) =>
        row.map((cell) => {
          if (typeof cell === "string") {
            const lines = doc.splitTextToSize(cell, maxWidth);
            return lines.join("\n");
          }
          return cell;
        }),
      );

      (doc as any).autoTable({
        ...defaultAutoTableOptions,
        head: [selectedHeaders],
        body: tableRows,
        styles: {
          overflow: "linebreak",
        },
      });

      const fileName = generateExportFileName("pdf");
      doc.save(fileName);

      dynamicToast.updateToast({
        severity: "success",
        content: `Export successful: ${fileName}`,
      });
    } catch (error) {
      console.error(error);
      dynamicToast.updateToast({
        severity: "error",
        content: "Export failed due to server error. Please try again.",
      });
    }
  };
  return (
    <div>
      <Box className="recordContainer">
        <Container maxWidth="xl">
          <Typography className="title">Audit Logs Dashboard</Typography>
          <Typography className={styles.subtext}>
            Helps to show the Audit logs
          </Typography>
          <Divider className={styles.divider} />
          <Box sx={{ flexGrow: 1 }}>
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <AuditLogFilter
                  filter={tempFilter}
                  onChange={setTempFilter}
                  onApply={applyHandler}
                />
              </Grid>
              <Grid item xs={6}>
                <Box
                  className={styles.container2}
                  sx={{ justifyContent: "flex-end" }}
                >
                  <TextField
                    placeholder="Search ..."
                    variant="outlined"
                    size="small"
                    className={styles.searchBar}
                    InputProps={{
                      startAdornment: <SearchIcon className="search-icon" />,
                    }}
                    data-testid="search-bar"
                    autoComplete="off"
                    value={searchData}
                    onChange={(e) => handleDataChange(e.target.value)}
                  />
                  <Box className={styles.actions}>
                    <Button
                      variant="outlined"
                      className={styles.actionBtn}
                      data-testid="export-button"
                      onClick={handleExportClick}
                      disabled={(allTotal ?? 0) === 0}
                    >
                      Export Audit Logs
                    </Button>
                  </Box>
                </Box>
              </Grid>
            </Grid>
          </Box>
          <Box className="tabHolder">
            <CustomTabs
              activeTab={activeTab}
              name="auditLogs"
              tabConfig={tabConfig}
              onTabChange={setActiveTab}
            />
          </Box>
        </Container>
        <Suspense fallback={<>Loading...</>}>
          <ExportRecord
            open={exportOpen}
            closeModal={() => setExportOpen(false)}
            downloadCSV={downloadCSV}
            downloadPDF={downloadPDF}
            requiredColumns={requiredAuditLogColumns}
          />
        </Suspense>
      </Box>
      <Footer isFixed={false} />
    </div>
  );
};

export default AuditLogs;
